import { useQuery } from '@tanstack/react-query';

import { API, getRequest } from '@/api/api.ts';
import type { EventResponse } from '@/store/events/events.dto.ts';
import type { TaskResponse } from '@/store/tasks/tasks.dto.ts';

/**
 * The task behind a notification, or `null` if it is gone — which is what
 * decides whether the notification renders a deep link at all (a deleted task
 * must not become a dead link). Any error (404, forbidden, offline) resolves to
 * `null` so we fail closed and show plain text instead of a broken link.
 *
 * The task itself, not just a yes/no: `taskHref` needs its `equipment` to know
 * which list the link should open, and this request already carries it.
 */
export const useTaskLinkQuery = (id?: string) =>
  useQuery({
    queryKey: ['task-link', id],
    queryFn: async () => {
      try {
        const response = await getRequest<TaskResponse>(API.ACTIVITY, `/tasks/${id}`);
        return response.data;
      } catch {
        return null;
      }
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

/**
 * Resolves whether an event still exists (used to decide if a notification should
 * deep-link to it — a deleted event must not become a dead link).
 * Any error (404, forbidden, offline) resolves to `false` so we fail closed.
 */
export const useEventExistsQuery = (id?: string) =>
  useQuery({
    queryKey: ['event-exists', id],
    queryFn: async () => {
      try {
        await getRequest<EventResponse>(API.ACTIVITY, `/events/${id}`);
        return true;
      } catch {
        return false;
      }
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
